import React from 'react';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';

/**
 * A single reading column, centred: the shape of every detail page that is
 * one thing top to bottom -- a booking's tracking page, its delivery note,
 * its dispute, the host's deposit.
 *
 * These were all written as a literal `maxWidth: 480` with no desktop
 * branch, which is a phone's width. On a 1920px monitor that is a narrow
 * strip floating in an ocean of white, with a map and a photo grid squeezed
 * into it for no reason. 480 was never a decision about these pages -- it is
 * simply the width they were designed at.
 *
 * So the cap only applies where there is room to spare, and it is wider
 * there. On a phone it never binds at all (no phone is 720px), which is why
 * the mobile layouts come through unchanged.
 */
export interface IPageColumnProps {
  children: React.ReactNode;
  /** Desktop cap. Raise it for a page with a map or a grid, lower it for one that is mostly prose. */
  maxWidth?: number;
  /** Merged last, so a caller can still set its own padding or margins. */
  style?: React.CSSProperties;
}

const DESKTOP_MAX_WIDTH = 720;

const PageColumn: React.FC<IPageColumnProps> = ({ children, maxWidth = DESKTOP_MAX_WIDTH, style }) => {
  const { isMobile } = useAppBreakpoint();

  return (
    <div
      style={{
        width: '100%',
        maxWidth,
        margin: '0 auto',
        padding: isMobile ? '0 4px' : '0 16px',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export default PageColumn;
